import { api } from './config';

export type ExportFormat = 'md' | 'html' | 'csv' | 'xlsx' | 'docx' | 'json' | 'pdf';

export interface AgentSession {
  id: number;
  session_id: string;
  title: string;
  message_count: number;
  is_active: boolean;
  context_type: string | null;
  context_id: number | null;
  created_at: string;
  updated_at: string;
  last_message_at: string | null;
}

export interface AgentAttachment {
  filename: string;
  mime_type: string;
  size: number;
  url: string;
}

export interface AgentMessage {
  id?: number;
  session_id: string;
  message: string;
  role: 'user' | 'assistant' | 'system' | 'tool';
  model_used?: string;
  tokens_used?: number;
  response_time_ms?: number;
  sources?: AgentSource[];
  attachments?: AgentAttachment[];
  created_at: string;
}

export interface AgentSource {
  chunk_id: number;
  document_id: number;
  title: string;
  similarity: number;
  preview?: string;
  chunk_index?: number;
}

export interface ChunkDetail {
  id: number;
  content: string;
  chunk_index: number;
  char_count: number;
  token_count: number;
  document_title: string;
  document_filename: string;
  document_file_type: string;
  created_at: string;
}

export interface AgentModel {
  id: number;
  provider: string;
  model_id: string;
  model_name: string;
  description?: string;
  is_default?: boolean;
}

export const agentApi = {
  getStatus: () =>
    api.get('/agent/status'),

  getModels: () =>
    api.get<{ data: AgentModel[] }>('/agent/models'),

  createSession: (data?: { context_type?: string; context_id?: number }) =>
    api.post('/agent/sessions', data || {}),

  getSessions: () =>
    api.get('/agent/sessions'),

  getSessionMessages: (sessionId: string) =>
    api.get(`/agent/sessions/${sessionId}/messages`),

  sendMessage: (sessionId: string, message: string, options?: { model_id?: string; enable_drive?: boolean; enable_notion?: boolean }) =>
    api.post(`/agent/sessions/${sessionId}/messages`, { message, ...options }, { timeout: 120000 }),

  sendMessageWithFiles: (sessionId: string, message: string, files: File[], options?: { model_id?: string; enable_drive?: boolean; enable_notion?: boolean }) => {
    const formData = new FormData();
    formData.append('message', message);
    files.forEach(file => formData.append('files', file));
    if (options?.model_id) formData.append('model_id', options.model_id);
    if (options?.enable_drive) formData.append('enable_drive', 'true');
    if (options?.enable_notion) formData.append('enable_notion', 'true');
    return api.post(`/agent/sessions/${sessionId}/messages`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 180000,
    });
  },

  deleteSession: (sessionId: string) =>
    api.delete(`/agent/sessions/${sessionId}`),

  clearSession: (sessionId: string) =>
    api.post(`/agent/sessions/${sessionId}/clear`),

  getChunkDetail: (chunkId: number) =>
    api.get(`/agent/chunks/${chunkId}`),

  embedMessage: (messageId: number) =>
    api.post(`/agent/messages/${messageId}/embed`),

  exportMessage: (messageId: number, format: ExportFormat) =>
    api.get(`/agent/messages/${messageId}/export`, {
      params: { format },
      responseType: 'blob',
    }),
};
